import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function PrivacyPage() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/pt')) i18n.changeLanguage('pt');
    else if (path.startsWith('/de')) i18n.changeLanguage('de');
    else i18n.changeLanguage('en');
  }, [location.pathname, i18n]);

  const langCode = i18n.language || 'en';

  const content: Record<string, { title: string; h1: string; body: string }> = {
    en: {
      title: 'Privacy Policy | CSSBuy Spreadsheet',
      h1: 'Privacy Policy',
      body: `CSSBuy Spreadsheet respects your privacy and is committed to protecting your personal information.

Information We Collect: This website may collect basic analytics data such as page views, visitor location (country level), and browser type through standard web analytics tools. We do not collect personally identifiable information unless voluntarily provided.

Cookies: We may use cookies to improve user experience and analyze website traffic. You can disable cookies in your browser settings if you prefer.

Third-Party Links: This website contains links to third-party websites. We are not responsible for the privacy practices or content of these external sites.

Data Security: We take reasonable measures to protect any information collected. However, no internet transmission is completely secure.

Contact: If you have any questions about this privacy policy, please contact us through the appropriate channels.

This privacy policy may be updated from time to time. Please check this page periodically for changes.`,
    },
    pt: {
      title: 'Política de Privacidade | CSSBuy Spreadsheet',
      h1: 'Política de Privacidade',
      body: `CSSBuy Spreadsheet respeita sua privacidade e está comprometido em proteger suas informações pessoais.

Informações que Coletamos: Este site pode coletar dados básicos de análise como visualizações de página, localização do visitante (nível de país) e tipo de navegador através de ferramentas padrão de análise web. Não coletamos informações pessoalmente identificáveis, a menos que sejam fornecidas voluntariamente.

Cookies: Podemos usar cookies para melhorar a experiência do usuário e analisar o tráfego do site. Você pode desativar cookies nas configurações do seu navegador se preferir.

Links de Terceiros: Este site contém links para sites de terceiros. Não somos responsáveis pelas práticas de privacidade ou conteúdo desses sites externos.

Segurança de Dados: Tomamos medidas razoáveis para proteger qualquer informação coletada. No entanto, nenhuma transmissão pela internet é completamente segura.

Contato: Se você tiver alguma dúvida sobre esta política de privacidade, entre em contato conosco através dos canais apropriados.

Esta política de privacidade pode ser atualizada de tempos em tempos. Por favor, verifique esta página periodicamente para alterações.`,
    },
    de: {
      title: 'Datenschutzrichtlinie | CSSBuy Spreadsheet',
      h1: 'Datenschutzrichtlinie',
      body: `CSSBuy Spreadsheet respektiert Ihre Privatsphäre und ist verpflichtet, Ihre persönlichen Informationen zu schützen.

Informationen, die wir sammeln: Diese Website kann grundlegende Analysedaten wie Seitenaufrufe, Besucherstandort (Landesebene) und Browsertyp durch Standard-Webanalyse-Tools erfassen. Wir sammeln keine persönlich identifizierbaren Informationen, es sei denn, sie werden freiwillig bereitgestellt.

Cookies: Wir können Cookies verwenden, um die Benutzererfahrung zu verbessern und Website-Traffic zu analysieren. Sie können Cookies in Ihren Browsereinstellungen deaktivieren, wenn Sie dies bevorzugen.

Drittanbieter-Links: Diese Website enthält Links zu Drittanbieter-Websites. Wir sind nicht für die Datenschutzpraktiken oder Inhalte dieser externen Seiten verantwortlich.

Datensicherheit: Wir treffen angemessene Maßnahmen, um gesammelte Informationen zu schützen. Allerdings ist keine Internetübertragung vollständig sicher.

Kontakt: Wenn Sie Fragen zu dieser Datenschutzrichtlinie haben, kontaktieren Sie uns bitte über die entsprechenden Kanäle.

Diese Datenschutzrichtlinie kann von Zeit zu Zeit aktualisiert werden. Bitte überprüfen Sie diese Seite regelmäßig auf Änderungen.`,
    },
  };

  const c = content[langCode] || content.en;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <header className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 lg:px-8 bg-background-100 border-b border-background-200">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {c.h1}
          </h1>
        </div>
      </header>

      <article className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <nav className="mb-8 text-sm text-foreground-500">
            <Link to={`/${langCode}/`} className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">Privacy</span>
          </nav>

          <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
            {c.body.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}